/**
 * The shape a chosen microphone, speaker or camera is stored in.
 *
 * It lives here rather than beside the matching in `media/` for the reason
 * `voiceCalibrationStore.ts` does: `settings.ts` is the bottom of the layer
 * graph and imports nothing else in `src/engine/`. How a stored choice is matched
 * to the hardware in front of somebody — and every rule about when one is
 * written — is `src/engine/media/deviceChoice.ts`.
 *
 * Three fields rather than an identifier, because an identifier is not an
 * identity. `deviceId` is issued per origin and per permission and rotates when
 * site data is cleared, so a stored one can name nothing while the hardware has
 * not moved. `groupId` and the label are what survive that, and storing them is
 * the whole reason a remembered headset is still found afterwards.
 */

/** One device somebody picked, stored so it can be found again. */
export interface DeviceChoice {
  /** What the browser called it when it was picked. May since have rotated. */
  deviceId: string;
  /** The group the browser put it in. Survives an identifier rotation. */
  groupId: string;
  /** What it was called. Empty where the browser was withholding names. */
  label: string;
}

/** The two a visitor can pick, and what is remembered about each. */
export interface DeviceChoices {
  audioInput: DeviceChoice | null;
  camera: DeviceChoice | null;
}

/** Nothing picked yet, which is every visitor until they pick. */
export const NO_DEVICE_CHOICES: DeviceChoices = {
  audioInput: null,
  camera: null,
};

/**
 * A stored value read back as a choice, or null for anything that is not one.
 *
 * Stored settings outlive the version that wrote them, and a value that cannot
 * be understood is a visitor who has not picked — never a crash, and never a
 * half-choice that matching would then act on.
 */
export function normalizeDeviceChoice(raw: unknown): DeviceChoice | null {
  if (!raw || typeof raw !== "object") return null;
  const source: Record<string, unknown> = Object.fromEntries(
    Object.entries(raw),
  );
  const { deviceId, groupId, label } = source;
  if (typeof deviceId !== "string" || deviceId === "") return null;
  return {
    deviceId,
    groupId: typeof groupId === "string" ? groupId : "",
    label: typeof label === "string" ? label : "",
  };
}
